// frontend/src/pages/dashboard/TeamPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography, Box, Grid, Card, CardContent, CardActions, Button, Chip, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, IconButton, Tooltip, FormControlLabel, Switch,
  Alert, Paper, CircularProgress, Checkbox, Collapse, InputAdornment, useMediaQuery,
  useTheme, Divider, Skeleton
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  AssignmentInd as AssignmentIndIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { teamMemberAPI, userAPI, roleAPI, permissionAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

// Modern color palette
const COLORS = {
  primary: "#4361ee",
  secondary: "#3f37c9",
  success: "#4cc9f0",
  warning: "#f72585",
  info: "#4895ef",
  light: "#f8f9fa",
  dark: "#212529",
  gray: "#6c757d",
  cardBg: "#ffffff",
  surface: "#f0f4f8"
};

const ROLE_PALETTE = [
  '#4361ee', '#3f37c9', '#4cc9f0', '#4895ef', '#4338ca',
  '#7e22ce', '#db2777', '#e11d48', '#059669', '#d97706'
];

const getRoleColor = (roleName, roleId) => {
  if (roleName?.trim().toLowerCase() === 'system administrator') return '#dc2626'; // red-600
  // Use ID modulo to cycle through palette consistently
  const index = roleId ? parseInt(roleId) % ROLE_PALETTE.length : 0;
  return ROLE_PALETTE[index] || ROLE_PALETTE[0];
};

const TeamPage = () => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [alertTimeout, setAlertTimeout] = useState(null);

  // Permission checks (centralized)
  const canCreateMember = authUser?.hasPermission('team.create');
  const canUpdateMember = authUser?.hasPermission('team.update');
  const canDeleteMember = authUser?.hasPermission('team.delete');
  const canManageRoles = authUser?.hasPermission('role.manage');
  const canViewPage = authUser?.hasPermission('team.view.page');
  const canViewDetails = authUser?.hasPermission('team.view.details');
  const isClient = !!authUser?.client_id;
  const isAdmin = authUser?.teamMembership?.role?.name?.toLowerCase() === 'system administrator';

  const [teamMembers, setTeamMembers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Member dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('add');
  const [currentMember, setCurrentMember] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', role: '', monthly_salary: 0 });
  const [saving, setSaving] = useState(false);
  const [memberRows, setMemberRows] = useState([
    {
      name: '',
      email: '',
      role: '',
      monthly_salary: '',
      job_title: '',
      joining_date: '',
      terminated_at: ''
    }]);
  const [rowsSaving, setRowsSaving] = useState(false);

  // Role dialog
  const [openRoleDialog, setOpenRoleDialog] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [newRoleName, setNewRoleName] = useState('');
  const [rolePermissions, setRolePermissions] = useState([]);
  const [savingRole, setSavingRole] = useState(false);

  // System administrator
  const isSystemAdmin = useCallback((member) => {
    return member.role?.name?.toLowerCase() === 'system administrator';
  }, []);

  // Permissions UI
  const [permissionSearch, setPermissionSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(['Project', 'Task', 'Client', 'Team']);

  const groupPermissionsByCategory = useCallback((perms) => {
    const groups = {};
    perms.forEach(perm => {
      let category = 'Uncategorized';
      if (perm.name.startsWith('dashboard.')) category = 'Dashboard';
      else if (perm.name.startsWith('client.')) category = 'Client';
      else if (perm.name.startsWith('team.')) category = 'Team';
      else if (perm.name.startsWith('status.')) category = 'Status';
      else if (perm.name.startsWith('project.')) category = 'Project';
      else if (perm.name.startsWith('task.')) category = 'Task';
      else if (perm.name.startsWith('projtaskect.')) category = 'Task';
      else if (perm.name.startsWith('project.board')) category = 'Project Board';
      else if (perm.name.startsWith('sprint.')) category = 'Sprint';
      else if (perm.name.startsWith('backlog.')) category = 'Backlog';
      else if (perm.name.startsWith('kpi.')) category = 'KPI';
      else if (perm.name.startsWith('calendar.')) category = 'Calendar';
      else if (
        perm.name.startsWith('package.') ||
        perm.name.startsWith('billing.') ||
        perm.name.startsWith('settings.')
      ) category = 'System & Billing';
      if (!groups[category]) groups[category] = [];
      groups[category].push(perm);
    });
    return Object.fromEntries(
      Object.entries(groups).sort(([a], [b]) =>
        a === 'Uncategorized' ? 1 : b === 'Uncategorized' ? -1 : a.localeCompare(b)
      )
    );
  }, []);

  const groupedPermissionData = useMemo(() => {
    return groupPermissionsByCategory(permissions);
  }, [permissions, groupPermissionsByCategory]);

  const handleViewTeamDetails = () => {
    if (isAdmin || canViewDetails) {
      navigate('/team-details');
    }
  };

  // Fetch data
  const fetchTeamMembers = useCallback(async () => {
    try {
      const res = await teamMemberAPI.getAll();
      const nonClientMembers = res.data.filter(member =>
        member.role?.name?.toLowerCase() !== 'client'
      );
      setTeamMembers(res.data);
    } catch {
      setError('Failed to load team members');
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await roleAPI.getAll();
      console.log('Roles:', res.data);
      setRoles(res.data);
      if (res.data.length && !formData.role) {
        setFormData(prev => ({ ...prev, role: res.data[0]?.name.toLowerCase() || 'member' }));
      }
    } catch {
      setError('Failed to load roles');
    }
  }, [formData.role]);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await permissionAPI.getAll();
      console.log('Permissions:', res.data);
      setPermissions(res.data);
    } catch {
      // Non-fatal
    }
  }, []);

  // Auto-dismiss alerts after 5 seconds
  useEffect(() => {
    if (error || success) {
      // Clear any existing timeout
      if (alertTimeout) {
        clearTimeout(alertTimeout);
      }

      // Set new timeout
      const timeout = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);

      setAlertTimeout(timeout);
    }

    // Cleanup on unmount or when alerts change
    return () => {
      if (alertTimeout) {
        clearTimeout(alertTimeout);
      }
    };
  }, [error, success]);

  // Updated useEffect to handle async loading correctly
  useEffect(() => {
    const loadData = async () => {
      if (!canViewPage) return;
      try {
        await Promise.all([fetchTeamMembers(), fetchRoles(), fetchPermissions()]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [canViewPage, fetchTeamMembers, fetchRoles, fetchPermissions]);

  const filteredTeamMembers = useMemo(() => {
    // if (isAdmin) {
    //   return teamMembers;
    // }
    return teamMembers.filter(member => !member.user?.client_id);
  }, [teamMembers, isAdmin]);

  // Member handlers
  const handleOpenDialog = (type = 'add', member = null) => {
    setDialogType(type);

    if (member) {
      // Edit mode - single member
      setCurrentMember(member);
      setMemberRows([{
        name: member.user.name,
        email: member.user.email,
        role: member.role?.name.toLowerCase() || 'member',
        monthly_salary: member.user.monthly_salary !== null
          ? Number(member.user.monthly_salary)
          : '',
        job_title: member.user.job_title || '',          // NEW
        joining_date: member.user.joining_date ? member.user.joining_date.split('T')[0] : '', // NEW
        terminated_at: member.user.terminated_at ? member.user.terminated_at.split('T')[0] : '' // NEW
      }]);
    } else {
      // Add mode - reset to single empty row
      setCurrentMember(null);
      setMemberRows([{ name: '', email: '', role: roles[0]?.name.toLowerCase() || 'member', monthly_salary: '' }]);
    }

    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'monthly_salary' ? (value === '' ? null : Number(value)) : value }));
  };


  // Add a new row
  const handleAddRow = () => {
    setMemberRows(prev => [...prev, { name: '', email: '', role: roles[0]?.name.toLowerCase() || 'member', monthly_salary: '' }]);
  };

  // Remove a specific row
  const handleRemoveRow = (index) => {
    setMemberRows(prev => prev.filter((_, i) => i !== index));
  };

  // Update specific row data
  const handleRowChange = (index, field, value) => {
    const newRows = [...memberRows];
    newRows[index][field] = field === 'monthly_salary' ? (value === '' ? '' : Number(value)) : value;
    setMemberRows(newRows);
  };

  // Submit (handles both single and multiple)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setRowsSaving(true);

    try {
      const validRows = memberRows.filter(r => r.name && r.email && r.role);

      if (validRows.length === 0) {
        setError('Please fill at least one member details');
        setRowsSaving(false);
        return;
      }

      if (dialogType === 'edit' && currentMember) {
        // Edit mode - only update first row
        const res = await teamMemberAPI.update(currentMember.id, validRows[0]);
        setTeamMembers(prev => prev.map(m => m.id === currentMember.id ? res.data : m));
        setSuccess('Team member updated successfully');
        setOpenDialog(false);
      } else {
        // Add mode - can be single or multiple
        const results = await Promise.allSettled(
          validRows.map(member =>
            userAPI.create({
              name: member.name,
              email: member.email,
              role: member.role,
              monthly_salary: member.monthly_salary === '' ? null : member.monthly_salary,
              job_title: member.job_title,
              joining_date: member.joining_date,
            })
          )
        );

        const successes = results.filter(r => r.status === 'fulfilled');
        const failures = results.filter(r => r.status === 'rejected');

        if (successes.length > 0) {
          await fetchTeamMembers();
          setSuccess(`${successes.length} member(s) added successfully`);
          setOpenDialog(false);
          setMemberRows([{ name: '', email: '', role: roles[0]?.name.toLowerCase() || 'member', monthly_salary: '' }]);
          setTimeout(() => {
            fetchTeamMembers();
          }, 500);
        }

        if (failures.length > 0) {
          setError(`${failures.length} member(s) failed. Check for duplicate emails.`);
        }
      }
    } catch (err) {
      let message = 'Operation failed. Please try again.';
      if (err.response?.data?.errors) {
        message = Object.values(err.response.data.errors).flat()[0];
      } else if (err.response?.data?.error) {
        message = err.response.data.error;
      }
      setError(message);
    } finally {
      setRowsSaving(false);
    }
  };

  const handleToggleStatus = async (member) => {
    if (!canUpdateMember) return;
    if (isSystemAdmin(member)) {
      setError('System Administrator cannot be deactivated');
      return;
    }
    try {
      const res = await teamMemberAPI.toggleStatus(member.id);
      setTeamMembers(prev => prev.map(m => m.id === member.id ? res.data : m));
    } catch {
      setError('Failed to update status');
    }
  };

  const handleRemoveMember = async (memberId) => {
    const member = teamMembers.find(m => m.id === memberId);
    if (!canDeleteMember || !window.confirm('Remove this team member?')) return;
    if (member && isSystemAdmin(member)) {
      setError('System Administrator cannot be removed');
      return;
    }
    try {
      await teamMemberAPI.delete(memberId);
      setTeamMembers(prev => prev.filter(m => m.id !== memberId));
      setSuccess('Team member removed successfully');
    } catch {
      setError('Failed to remove team member');
    }
  };

  // Role handlers
  const handleOpenRoleDialog = (role = null) => {
    if (!canManageRoles) return;
    const isDefaultAdmin = role?.name?.toLowerCase() === 'system administrator';
    if (isDefaultAdmin) {
      setError('Default Admin role cannot be edited');
      return;
    }
    setCurrentRole(role);
    setNewRoleName(role?.name || '');
    setRolePermissions(role?.permissions?.map(p => p.id) || []);
    setPermissionSearch('');
    setOpenRoleDialog(true);
    setError('');
    setSuccess('');
  };

  const handleRoleSubmit = async () => {
    if (!canManageRoles) return;
    if (!newRoleName.trim()) {
      setError('Role name is required');
      return;
    }
    if (newRoleName.toLowerCase() === 'system administrator') {
      setError('Cannot create or rename to "System Administrator"');
      return;
    }
    setSavingRole(true);
    try {
      if (!currentRole) {
        const res = await roleAPI.create({ name: newRoleName });
        await roleAPI.updatePermissions(res.data.id, { permission_ids: rolePermissions });
        setRoles(prev => [...prev, { ...res.data, permissions: permissions.filter(p => rolePermissions.includes(p.id)) }]);
        setSuccess('Role created successfully');
      } else {
        const res = await roleAPI.update(currentRole.id, { name: newRoleName });
        await roleAPI.updatePermissions(currentRole.id, { permission_ids: rolePermissions });
        setRoles(prev => prev.map(r => r.id === currentRole.id ? { ...res.data, permissions: permissions.filter(p => rolePermissions.includes(p.id)) } : r));
        setSuccess('Role updated successfully');
      }
      setOpenRoleDialog(false);
    } catch (err) {
      let message = 'Failed to save role';
      if (err.response?.data?.errors) {
        message = Object.values(err.response.data.errors).flat()[0];
      } else if (err.response?.data?.error) {
        message = err.response.data.error;
      }
      setError(message);
    } finally {
      setSavingRole(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!canManageRoles) return;
    const role = roles.find(r => r.id === roleId);
    if (role?.name?.toLowerCase() === 'admin') {
      setError('Default Admin role cannot be deleted');
      return;
    }
    if (!window.confirm(`Delete role "${role?.name}"? This may affect existing members.`)) return;
    try {
      await roleAPI.delete(roleId);
      setRoles(prev => prev.filter(r => r.id !== roleId));
      setTeamMembers(prev =>
        prev.map(m => (m.role?.id === roleId ? { ...m, role: { name: 'Member', id: null } } : m))
      );
      setSuccess('Role deleted successfully');
    } catch (err) {
      let message = 'Failed to delete role';
      if (err.response?.data?.error) message = err.response.data.error;
      setError(message);
    }
  };

  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    const baseUrl = process.env.REACT_APP_AVATAR_URL?.replace(/\/+$/, '') || 'http://localhost:8000/storage';
    const cleanPath = avatarPath.replace(/^\/+/, '');
    return `${baseUrl}/${cleanPath}?t=${Date.now()}`;
  };

  const sharedButtonStyles = {
    height: 48,
    px: 3,
    borderRadius: 3,
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'none',
    transition: 'all 0.2s ease-in-out',
    width: isMobile ? "100%" : "auto",
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiButton-startIcon': {
      '& > *:nth-of-type(1)': {
        fontSize: isMobile ? 18 : 20,
      },
    },
  };

  // Mobile-friendly team member rendering
  const renderedTeam = useMemo(() => {
    if (isMobile) {
      return filteredTeamMembers.sort((a, b) => a.user.name.localeCompare(b.user.name)).map(member => {
        const roleName = member.role?.name || 'Member';
        const protectedAdmin = isSystemAdmin(member);
        return (
          <Card
            key={member.id}
            variant="outlined"
            sx={{
              mb: 1,
              borderRadius: 2,
              boxShadow: `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)'}`,
              border: `1px solid ${theme.palette.divider}`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 16px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.1)'}`
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar
                  sx={{
                    width: isMobile ? 48 : 64,
                    height: isMobile ? 48 : 64,
                    bgcolor: member.user.avatar ? 'transparent' : getRoleColor(roleName, member.role?.id),
                    color: 'white',
                    fontWeight: 600,
                    mb: 1
                  }}
                  src={getAvatarUrl(member.user.avatar)}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = null;
                  }}
                >
                  {!member.user.avatar && member.user.name
                    ? member.user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                    : null
                  }
                </Avatar>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>{member.user.name}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>{member.user.email}</Typography>
                  <Chip
                    label={roleName}
                    size="small"
                    sx={{
                      mt: 1,
                      fontWeight: 600,
                      textTransform: 'capitalize',
                      bgcolor: getRoleColor(roleName, member.role?.id),
                      color: 'white',
                      fontSize: '0.75rem'
                    }}
                  />
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={member.is_active}
                      onChange={() => handleToggleStatus(member)}
                      disabled={!canUpdateMember || protectedAdmin}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500} color={member.is_active ? 'text.primary' : 'text.secondary'}>
                      {member.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ ml: 0 }}
                />
                <Box>
                  {canUpdateMember && !protectedAdmin && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog('edit', member)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDeleteMember && !protectedAdmin && (
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => handleRemoveMember(member.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      });
    } else {
      return filteredTeamMembers.sort((a, b) => a.user.name.localeCompare(b.user.name)).map(member => {
        const roleName = member.role?.name || 'Member';
        const protectedAdmin = isSystemAdmin(member);
        return (
          <Grid item size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={member.id}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)'}`,
                border: `1px solid ${theme.palette.divider}`,
                opacity: member.is_active ? 1 : 0.75,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 6px 16px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.1)'}`
                }
              }}
            >
              <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', pb: 1 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    mb: 1,
                    bgcolor: member.user.avatar ? 'transparent' : getRoleColor(roleName, member.role?.id),
                    color: 'white',
                    fontWeight: 600
                  }}
                  src={
                    member.user.avatar
                      ? `${process.env.REACT_APP_AVATAR_URL.replace(/\/?$/, '/')}${member.user.avatar}?t=${new Date().getTime()}`
                      : null
                  }
                >
                  {!member.user.avatar && member.user.name
                    ? member.user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                    : null
                  }
                </Avatar>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>{member.user.name}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>{member.user.email}</Typography>
                <Chip
                  label={roleName}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    bgcolor: getRoleColor(roleName, member.role?.id),
                    color: 'white',
                    mb: 1
                  }}
                />
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 1.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={member.is_active}
                      onChange={() => handleToggleStatus(member)}
                      disabled={!canUpdateMember || protectedAdmin}
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500} color={member.is_active ? 'text.primary' : 'text.secondary'}>
                      {member.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  }
                  labelPlacement="start"
                  sx={{ ml: 0 }}
                />
                <Box>
                  {canUpdateMember && !protectedAdmin && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenDialog('edit', member)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDeleteMember && !protectedAdmin && (
                    <Tooltip title="Remove">
                      <IconButton size="small" color="error" onClick={() => handleRemoveMember(member.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </CardActions>
            </Card>
          </Grid>
        );
      });
    }
  }, [teamMembers, canUpdateMember, canDeleteMember, isMobile, theme.palette.mode]);

  // Skeleton Loader Component
  const renderSkeletons = () => (
    <>
      {/* Setup Guide Skeleton */}
      {canManageRoles && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="text" width="60%" height={20} />
        </Paper>
      )}

      {/* Roles Section Skeleton */}
      {canManageRoles && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width={150} height={25} />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" width={100} height={32} />
            ))}
          </Box>
        </Box>
      )}

      {/* Team Members Section Skeleton */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={20} height={20} />
        <Skeleton variant="text" width={150} height={25} />
      </Box>
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
            <Card variant="outlined" sx={{ borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Skeleton variant="circular" width={64} height={64} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="60%" height={15} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width={80} height={24} />
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <Box p={3}>
          <Alert severity="error">You do not have permission to view the Team page.</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{
        p: { xs: 1, sm: 2, md: 3 },
        background: COLORS.surface,
        minHeight: '100vh',
        pb: 10
      }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: 1
        }}>
          <Box>
            <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon sx={{ color: COLORS.primary }} />
              Team Management
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: isMobile ? 'space-between' : 'flex-end' }}>
            {canManageRoles && (
              <Button
                variant="outlined"
                color="primary"
                size={isMobile ? "small" : "medium"}
                sx={{
                  ...sharedButtonStyles,
                  background: `linear-gradient(135deg, ${theme.palette.primary.light}40 0%, ${theme.palette.primary.main}20 100%)`,
                  color: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}30`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.light}60 0%, ${theme.palette.primary.main}40 100%)`,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    transform: 'translateY(-1px)',
                    borderColor: theme.palette.primary.main,
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                  },
                }}
                startIcon={<LockIcon />}
                onClick={() => handleOpenRoleDialog()}
              >
                Add Role
              </Button>
            )}
            {canCreateMember && (
              <Button
                variant="contained"
                color="primary"
                size={isMobile ? "small" : "medium"}
                startIcon={<PersonAddIcon />}
                onClick={() => handleOpenDialog('add')}
                sx={{
                  height: 48,
                  px: 3,
                  borderRadius: 3,
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
                    transform: 'translateY(-1px)',
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  width: isMobile ? "100%" : "auto",
                }}
              >
                Add Member
              </Button>
            )}
            {!isClient && (canViewDetails || isAdmin) && (
              <Button
                variant="contained"
                color="info"
                size={isMobile ? "small" : "medium"}
                startIcon={<VisibilityIcon />}
                onClick={handleViewTeamDetails}
                sx={{
                  height: 48,
                  px: 3,
                  borderRadius: 3,
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
                  '&:hover': {
                    boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
                    transform: 'translateY(-1px)',
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  width: isMobile ? "100%" : "auto",
                }}
              >
                Team Details
              </Button>
            )}
          </Box>
        </Box>

        {/* Conditional Content: Skeletons vs Real Data */}
        {loading ? (
          renderSkeletons()
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

            {/* Setup Guide */}
            {canManageRoles && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  borderLeft: 4,
                  borderColor: COLORS.info,
                  backgroundColor: `${COLORS.info}10`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <AssignmentIndIcon sx={{ color: COLORS.info }} />
                <Typography variant="body2" color="text.primary" sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
                  <strong>Workflow:</strong> First Add roles → then add members
                </Typography>
              </Paper>
            )}

            {/* Roles Section */}
            {canManageRoles && roles.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                  <LockIcon sx={{ color: COLORS.secondary }} />
                  Custom Roles
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {roles.sort((a, b) => a.name.localeCompare(b.name)).map((role) => {
                    const isDefaultAdmin = role.name.toLowerCase() === 'system administrator';
                    return (
                      <Chip
                        key={role.id}
                        label={role.name}
                        variant="outlined"
                        onClick={() => !isDefaultAdmin && handleOpenRoleDialog(role)}
                        onDelete={() => !isDefaultAdmin && handleDeleteRole(role.id)}
                        deleteIcon={<DeleteIcon />}
                        sx={{
                          fontWeight: 500,
                          textTransform: 'capitalize',
                          cursor: isDefaultAdmin ? 'default' : 'pointer',
                          opacity: isDefaultAdmin ? 0.7 : 1,
                          pointerEvents: isDefaultAdmin ? 'none' : 'auto',
                          '&:hover': !isDefaultAdmin ? { bgcolor: 'action.hover' } : {},
                          fontSize: isMobile ? '0.75rem' : '0.875rem',
                          padding: isMobile ? '4px 8px' : '6px 12px',
                          borderColor: getRoleColor(role.name, role.id),
                          color: getRoleColor(role.name, role.id),
                          '&:hover': !isDefaultAdmin ? {
                            bgcolor: `${getRoleColor(role.name, role.id)}10`,
                            borderColor: getRoleColor(role.name, role.id)
                          } : {}
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Team Members */}
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
              <PersonIcon sx={{ color: COLORS.primary }} />
              Team Members
            </Typography>
            {filteredTeamMembers.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                  backgroundColor: COLORS.cardBg
                }}
              >
                <PersonAddIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography color="text.secondary">No team members found</Typography>
              </Paper>
            ) : isMobile ? (
              <Box>
                {renderedTeam}
              </Box>
            ) : (
              <Grid container spacing={3}>{renderedTeam}</Grid>
            )}
          </>
        )}

        {/* Member Dialog */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: `0 10px 25px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.15)'}`
            }
          }}
        >
          <DialogTitle sx={{ pb: 1, fontWeight: 700, color: 'text.primary', borderBottom: `1px solid ${theme.palette.divider}` }}>
            {dialogType === 'add'
              ? (memberRows.length > 1 ? `Add ${memberRows.length} Members` : 'Add Team Member')
              : 'Edit Team Member'}
          </DialogTitle>

          <form onSubmit={handleSubmit}>
            <DialogContent sx={{ pt: 3, maxHeight: '70vh', overflow: 'auto' }}>
              {dialogType === 'add' && (
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem', display: 'none' }}>
                  Add one or multiple members. Duplicate emails will be skipped.
                </Alert>
              )}

              {memberRows.map((row, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    p: 1.5,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    flexWrap: 'wrap',
                    mb: 1
                  }}
                >
                  {/* Row Number */}
                  {memberRows.length > 1 && (
                    <Typography variant="subtitle2" sx={{ minWidth: 25, color: 'text.secondary' }}>
                      {index + 1}.
                    </Typography>
                  )}

                  {/* Name Field */}
                  <TextField
                    size="small"
                    label={index === 0 ? "Full Name *" : undefined}
                    placeholder={index === 0 ? undefined : "Name"}
                    value={row.name}
                    onChange={(e) => handleRowChange(index, 'name', e.target.value)}
                    sx={{ flex: 2, minWidth: 120 }}
                    required
                  />

                  {/* Email Field */}
                  <TextField
                    size="small"
                    label={index === 0 ? "Email *" : undefined}
                    placeholder={index === 0 ? undefined : "Email"}
                    type="email"
                    value={row.email}
                    onChange={(e) => handleRowChange(index, 'email', e.target.value)}
                    sx={{ flex: 3, minWidth: 150 }}
                    required
                  />

                  {/* Role Field - Fixed Placeholder */}
                  <FormControl size="small" sx={{ flex: 2, minWidth: 120 }}>
                    {index === 0 && (
                      <InputLabel shrink>Role *</InputLabel>
                    )}
                    <Select
                      size="small"
                      value={row.role || ''}
                      onChange={(e) => handleRowChange(index, 'role', e.target.value)}
                      displayEmpty
                      sx={{ minWidth: 100, height: 40 }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return <em style={{ color: 'text.secondary' }}>Role</em>;
                        }
                        const role = roles.find(r => r.name.toLowerCase() === selected);
                        return role?.name || selected;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select Role</em>
                      </MenuItem>
                      {roles.map((r) => (
                        <MenuItem key={r.id} value={r.name.toLowerCase()} disabled={r.name.toLowerCase() === 'system administrator'}>
                          {r.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Salary Field */}
                  <TextField
                    size="small"
                    label={index === 0 && authUser?.teamMembership?.role?.name?.toLowerCase() === "system administrator" ? "Salary *" : undefined}
                    placeholder={index === 0 ? "Salary" : undefined}
                    type="number"
                    value={row.monthly_salary}
                    onChange={(e) => handleRowChange(index, 'monthly_salary', e.target.value)}
                    sx={{ flex: 1, minWidth: 100 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      sx: { height: 40 }
                    }}
                  />
                  {/* Designation Field */}
                  <TextField
                    size="small"
                    label={index === 0 ? "Designation" : undefined}
                    placeholder={index === 0 ? undefined : "Designation"}
                    value={row.job_title}
                    onChange={(e) => handleRowChange(index, 'job_title', e.target.value)}
                    sx={{ flex: 2, minWidth: 120 }}
                  />
                  {/* Joining Date Field */}
                  <TextField
                    size="small"
                    label={index === 0 ? "Joining Date" : undefined}
                    placeholder={index === 0 ? undefined : "Joining Date"}
                    type="date"
                    value={row.joining_date}
                    onChange={(e) => handleRowChange(index, 'joining_date', e.target.value)}
                    sx={{ flex: 2, minWidth: 120 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  {/* Terminated Date Field (Only for Edit Mode & Admin) */}
                  {dialogType === 'edit' && isAdmin && (
                    <TextField
                      size="small"
                      label="Terminated Date"
                      type="date"
                      value={row.terminated_at}
                      onChange={(e) => handleRowChange(index, 'terminated_at', e.target.value)}
                      sx={{ flex: 2, minWidth: 120 }}
                      InputLabelProps={{ shrink: true }}
                    //helperText="Leave empty if active"
                    />
                  )}


                  {/* Remove Button */}
                  {memberRows.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveRow(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}

              {/* Add Row Button - Only in Add Mode */}
              {dialogType === 'add' && (
                <Button
                  startIcon={<PersonAddIcon />}
                  onClick={handleAddRow}
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 1, borderStyle: 'dashed', display: 'none' }}
                >
                  Add Another Member
                </Button>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Button
                onClick={() => setOpenDialog(false)}
                color="inherit"
                disabled={rowsSaving}
                sx={{ textTransform: 'none', color: COLORS.gray }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={rowsSaving}
                sx={{ textTransform: 'none', bgcolor: COLORS.primary, '&:hover': { bgcolor: COLORS.secondary } }}
              >
                {rowsSaving
                  ? (dialogType === 'add' ? 'Adding...' : 'Updating...')
                  : (dialogType === 'add'
                    ? (memberRows.length > 1 ? `Add ${memberRows.length} Members` : 'Add Member')
                    : 'Update Member')
                }
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Role Dialog */}
        <Dialog
          open={openRoleDialog}
          onClose={() => setOpenRoleDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: `0 10px 25px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.15)'}`
            }
          }}
        >
          <DialogTitle sx={{ pb: 1, fontWeight: 700, color: 'text.primary', borderBottom: `1px solid ${theme.palette.divider}` }}>
            {currentRole ? 'Edit Role' : 'Create New Role'}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 2 }}>
            <TextField
              label="Role Name *"
              fullWidth
              margin="dense"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              required
              autoFocus
              error={newRoleName.toLowerCase() === 'system administrator'}
              helperText={newRoleName.toLowerCase() === 'system administrator' ? 'Cannot use "System Administrator" as role name' : ''}
              InputProps={{
                sx: {
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`
                }
              }}
            />
            <Box sx={{ mt: 2, mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search permissions..."
                value={permissionSearch}
                onChange={(e) => setPermissionSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
                sx={{
                  flex: 1,
                  minWidth: 200,
                  maxWidth: 300,
                  borderRadius: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`
                  }
                }}
              />
              <Chip
                label={`${rolePermissions.length} selected`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  borderColor: COLORS.primary,
                  color: COLORS.primary
                }}
              />
            </Box>
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ color: 'text.primary' }}>
                Permissions ({permissions.length} total)
              </Typography>
              {permissions.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No permissions available. <Button size="small" onClick={() => navigate('/permissions')} sx={{ textTransform: 'none' }}>Create some first</Button>
                </Alert>
              ) : (
                <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 1 }}>
                  {Object.entries(groupedPermissionData).map(([category, perms]) => {
                    const filteredPerms = perms.filter(p =>
                      !permissionSearch ||
                      p.display_name.toLowerCase().includes(permissionSearch.toLowerCase()) ||
                      p.name.toLowerCase().includes(permissionSearch.toLowerCase())
                    );
                    if (filteredPerms.length === 0) return null;
                    const allSelected = filteredPerms.every(p => rolePermissions.includes(p.id));
                    const someSelected = filteredPerms.some(p => rolePermissions.includes(p.id)) && !allSelected;
                    return (
                      <Paper
                        key={category}
                        variant="outlined"
                        sx={{
                          mb: 1.5,
                          borderRadius: 1.5,
                          borderLeft: category === 'Uncategorized' ? '3px solid' : undefined,
                          borderColor: category === 'Uncategorized' ? COLORS.warning : 'divider',
                          backgroundColor: theme.palette.background.paper
                        }}
                      >
                        <Box
                          onClick={() => setExpandedCategories(prev =>
                            prev.includes(category)
                              ? prev.filter(c => c !== category)
                              : [...prev, category]
                          )}
                          sx={{
                            p: 1,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: category === 'Uncategorized' ? `${COLORS.warning}10` : 'transparent'
                          }}
                        >
                          <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setRolePermissions(prev => [
                                  ...new Set([...prev, ...filteredPerms.map(p => p.id)])
                                ]);
                              } else {
                                setRolePermissions(prev =>
                                  prev.filter(id => !filteredPerms.map(p => p.id).includes(id))
                                );
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                          />
                          <Typography fontWeight={600} sx={{ flexGrow: 1, color: 'text.primary' }}>
                            {category} ({filteredPerms.length})
                          </Typography>
                          {category === 'Uncategorized' && (
                            <WarningIcon color="warning" fontSize="small" sx={{ mr: 0.5 }} />
                          )}
                          <IconButton size="small" onClick={(e) => {
                            e.stopPropagation();
                            setExpandedCategories(prev =>
                              prev.includes(category)
                                ? prev.filter(c => c !== category)
                                : [...prev, category]
                            );
                          }}>
                            <ExpandMoreIcon
                              sx={{
                                transform: expandedCategories.includes(category) ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                              }}
                            />
                          </IconButton>
                        </Box>
                        <Collapse in={expandedCategories.includes(category)}>
                          <Box sx={{ p: 1, pt: 0 }}>
                            {filteredPerms.map(p => (
                              <FormControlLabel
                                key={p.id}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={rolePermissions.includes(p.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setRolePermissions(prev => [...prev, p.id]);
                                      } else {
                                        setRolePermissions(prev => prev.filter(id => id !== p.id));
                                      }
                                    }}
                                  />
                                }
                                label={
                                  <Tooltip title={p.name} arrow>
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 250, color: 'text.primary' }}>
                                      {p.display_name}
                                    </Typography>
                                  </Tooltip>
                                }
                                sx={{
                                  ml: 1,
                                  '& .MuiFormControlLabel-label': { fontSize: '0.875rem' }
                                }}
                              />
                            ))}
                          </Box>
                        </Collapse>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Button
              onClick={() => setOpenRoleDialog(false)}
              color="inherit"
              sx={{
                textTransform: 'none',
                color: COLORS.gray
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleRoleSubmit}
              disabled={savingRole}
              sx={{
                textTransform: 'none',
                bgcolor: COLORS.secondary,
                '&:hover': {
                  bgcolor: COLORS.primary
                }
              }}
            >
              {savingRole ? (currentRole ? 'Updating…' : 'Creating…') : (currentRole ? 'Update Role' : 'Create Role')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
};

export default TeamPage;